import { z } from 'zod';
import { CURRENCIES, money } from './sales';

/**
 * Money in and out. Collections settle a sale's price, per currency; supplier
 * payments settle services' cost and are spread over the services they pay for.
 * Both are voided, never edited or deleted.
 */

const id = z.uuid();
const version = z.number().int().positive();
const blankToNull = <T>(v: T) => (v === '' ? null : v);
const optional = (max: number) =>
  z.string().trim().max(max).nullable().optional().transform(blankToNull);
const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Tarihi seçin');
const positive = money.refine((v) => Number(v) > 0, 'Tutar sıfırdan büyük olmalı');

export const COLLECTION_METHODS = [
  'cash',
  'bank_transfer',
  'credit_card',
  'virtual_pos',
  'other',
] as const;
export type CollectionMethod = (typeof COLLECTION_METHODS)[number];
export const COLLECTION_METHOD_LABELS: Record<CollectionMethod, string> = {
  cash: 'Nakit',
  bank_transfer: 'Havale / EFT',
  credit_card: 'Kredi kartı',
  virtual_pos: 'Sanal POS',
  other: 'Diğer',
};

export const SUPPLIER_PAYMENT_METHODS = [
  'bank_transfer',
  'company_card',
  'cash',
  'offset',
  'other',
] as const;
export type SupplierPaymentMethod = (typeof SUPPLIER_PAYMENT_METHODS)[number];
export const SUPPLIER_PAYMENT_METHOD_LABELS: Record<SupplierPaymentMethod, string> = {
  bank_transfer: 'Havale / EFT',
  company_card: 'Şirket kartı',
  cash: 'Nakit',
  offset: 'Mahsup',
  other: 'Diğer',
};

export const CARD_NETWORKS = ['visa', 'mastercard', 'amex', 'troy', 'other'] as const;
export const CARD_NETWORK_LABELS: Record<(typeof CARD_NETWORKS)[number], string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'American Express',
  troy: 'Troy',
  other: 'Diğer',
};

export const createCollectionInput = z.object({
  paidOn: day,
  currency: z.enum(CURRENCIES),
  amount: positive,
  method: z.enum(COLLECTION_METHODS),
  reference: optional(80),
  notes: optional(1000),
});
export type CreateCollectionInput = z.input<typeof createCollectionInput>;

export const voidInput = z.object({
  reason: z.string().trim().min(3, 'İptal nedenini yazın').max(300),
});

export const createSupplierPaymentInput = z
  .object({
    paidOn: day,
    currency: z.enum(CURRENCIES),
    amount: positive,
    method: z.enum(SUPPLIER_PAYMENT_METHODS),
    cardId: id
      .nullable()
      .optional()
      .or(z.literal('').transform(() => null)),
    reference: optional(80),
    notes: optional(1000),
    allocations: z
      .array(z.object({ serviceId: id, amount: positive }))
      .max(200)
      .default([]),
  })
  .superRefine((p, ctx) => {
    if (p.method === 'company_card' && !p.cardId) {
      ctx.addIssue({ code: 'custom', path: ['cardId'], message: 'Ödemenin yapıldığı kartı seçin' });
    }
    const total = p.allocations.reduce((sum, a) => sum + Number(a.amount), 0);
    if (total > Number(p.amount) + 0.001) {
      ctx.addIssue({
        code: 'custom',
        path: ['allocations'],
        message: 'Hizmetlere dağıtılan tutar ödemeyi aşıyor',
      });
    }
    const seen = new Set<string>();
    p.allocations.forEach((a, i) => {
      if (seen.has(a.serviceId)) {
        ctx.addIssue({
          code: 'custom',
          path: ['allocations', i],
          message: 'Bu hizmet iki kez seçilmiş',
        });
      }
      seen.add(a.serviceId);
    });
  });
export type CreateSupplierPaymentInput = z.input<typeof createSupplierPaymentInput>;

const cardFields = {
  name: z.string().trim().min(1, 'Kartın adını yazın').max(80),
  holderName: optional(120),
  bank: optional(80),
  network: z.enum(CARD_NETWORKS).default('other'),
  last4: z
    .string()
    .trim()
    .regex(/^\d{4}$/, 'Kartın son dört hanesini yazın'),
};
export const createCardInput = z.object(cardFields);
export const updateCardInput = z.object(cardFields).partial().extend({ version });

const range = {
  from: day.optional(),
  to: day.optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
};
export const collectionListQuery = z.object({
  ...range,
  method: z.enum(COLLECTION_METHODS).optional(),
  currency: z.enum(CURRENCIES).optional(),
  voided: z.enum(['true', 'false']).optional(),
});
export const supplierPaymentListQuery = z.object({
  ...range,
  supplierId: id.optional(),
  method: z.enum(SUPPLIER_PAYMENT_METHODS).optional(),
  currency: z.enum(CURRENCIES).optional(),
  voided: z.enum(['true', 'false']).optional(),
});
export const receivablesQuery = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});

// Reminders ----------------------------------------------------------------------------

export const reminderSettingsInput = z.object({
  optionReminderHours: z
    .array(z.number().int().min(1, 'En az 1 saat').max(720, 'En fazla 30 gün (720 saat)'))
    .max(6, 'En fazla altı hatırlatma')
    .transform((hours) => [...new Set(hours)].sort((a, b) => b - a)),
  checkinLeadHours: z.number().int().min(1).max(72, 'En fazla 72 saat'),
  version,
});

export const notificationPrefsInput = z.object({ emailNotifications: z.boolean() });

export const checkinListQuery = z.object({
  /** How far ahead to look, in hours. */
  hours: z.coerce
    .number()
    .int()
    .min(1)
    .max(24 * 14)
    .default(72),
  done: z.enum(['true', 'false', 'all']).default('all'),
});

export const optionListQuery = z.object({
  mine: z.enum(['true', 'false']).optional(),
});
